const ENVIRONMENTS = ["local", "development", "production"] as const;
type Environment = (typeof ENVIRONMENTS)[number];

const isEnvironment = (value: unknown): value is Environment =>
  typeof value === "string" &&
  (ENVIRONMENTS as readonly string[]).includes(value);

/**
 * The business unit this deployment trades as. lazerhub is shared with Skynet
 * (the European desk, "mft1") and uses this to decide which exchanges a request
 * sees — mftapac3 resolves to JEPX only. Sent as the `company` query parameter on
 * every lazerhub request; omitting it would fall back to mft1 and target Europe.
 */
export type Company = "mft1" | "mftapac3";
export const COMPANY: Company = "mftapac3";

/**
 * Environment override, chosen at build time rather than edited by hand:
 *
 *   npm start -- local            # or development / production
 *   MFT_ENVIRONMENT=local npm start
 *
 * `null` when no override was passed, which is the case for every deployed
 * build — those read the runtime value instead, see {@link getEnvironment}.
 *
 * Note that pinning `production` locally points the app at the production
 * lazerhub and NATS, so treat it as read-only unless you mean it.
 */
function overrideEnvironment(): Environment | null {
  return __MFT_ENVIRONMENT__;
}

export const getNatsUrl = () => {
  switch (getEnvironment()) {
    case "local":
      // Local NATS (docker, JetStream + plain-ws for the browser).
      return "ws://localhost:7443";
    case "development":
      return "wss://lazertrader.dev.mft-energy.com:7443";
    case "production":
      return "wss://lazertrader.prod.mft-energy.com:7443";
  }
};

export function getLazerHubBaseUri(): string {
  switch (getEnvironment()) {
    case "local":
      // 8090, not 8080: claude-butler squats 127.0.0.1:8080 on this machine.
      return "http://localhost:8090";
    case "development":
      return "https://lazertrader.dev.mft-energy.com";
    case "production":
      return "https://lazertrader.prod.mft-energy.com";
  }
}

/**
 * jepx-data-provider: JEPX day-ahead spot prices, read from TradeData. This is
 * a separate backend from lazerhub, so it needs both its own base URI and its
 * own access token — see {@link getAzureJepxDataProviderScopes}.
 *
 * The local port matches the function app's launchSettings profile.
 */
export function getJepxDataProviderBaseUri(): string {
  switch (getEnvironment()) {
    case "local":
      return "http://localhost:7263";
    case "development":
      return "https://func-mftapac3-jepx-data-provider-d.azurewebsites.net";
    case "production":
      return "https://func-mftapac3-jepx-data-provider-p.azurewebsites.net";
  }
}

/**
 * Scope for jepx-data-provider. Tokens are audience-scoped, so this cannot
 * reuse the algo API token; MSAL issues a separate one for the same account.
 *
 * These must line up with the app registration each function app's Easy Auth is
 * configured with, or Easy Auth answers 401 with an empty body before the
 * request ever reaches the function:
 *
 *   func-…-d  ->  fc4c27a3-291c-42dd-9cdc-ed292f3edcc7  (…-provider-d)
 *   func-…-p  ->  40fbe0c4-16d6-48d6-bf93-9579d1583d15  (…-provider-p)
 *
 * Note the audience in the issued token is the bare client-id GUID, not the
 * `api://` URI used to request it.
 */
function getAzureJepxDataProviderScopes(): string {
  switch (getEnvironment()) {
    case "local":
      // The local function app has no Easy Auth in front of it — LocalDevAuthShim
      // synthesises the principal — but keep the dev audience so a local SPA can
      // also be pointed at the deployed dev app without a config change.
      return "api://mftapac3-jepx-data-provider-d/access_as_user";
    case "development":
      return "api://mftapac3-jepx-data-provider-d/access_as_user";
    case "production":
      return "api://mftapac3-jepx-data-provider-p/access_as_user";
  }
}

/**
 * The SPA's app registration. MSAL cannot recover from a wrong value here: Entra
 * answers `AADSTS700016` (application not found), which is *not* an
 * `InteractionRequiredAuthError`, so `AuthWrapper` logs it and never falls back
 * to a redirect — the app parks on "Signing in…" indefinitely.
 *
 * `25018065-…` (local/dev) and `933b81e4-…` (prod) were set in ea9a239 but do
 * not exist in tenant 7a7dc48e, which is what broke login everywhere. These are
 * Skynet's registrations, shared until this desk gets its own: `algoApp -
 * dev/test` already carries `http://localhost:3000` as an SPA redirect URI and
 * delegated access to `api://a144bf85-…`, so local development works with it.
 *
 * NOTE: production is unverified. `entraapp-mftit-jepx-lazer-desk-{d,p}` exist
 * in Entra but are wired for App Service Easy Auth — `web` redirect URIs
 * pointing at `/.auth/login/aad/callback`, no SPA platform, no API permissions —
 * so they cannot serve as the SPA client without being extended first.
 */
function getAzureClientId(): string {
  switch (getEnvironment()) {
    case "local":
      return "979eb9c8-b3d5-4d7c-92c4-8a3c727393d9";
    case "development":
      return "979eb9c8-b3d5-4d7c-92c4-8a3c727393d9";
    case "production":
      return "65d7d763-3865-4fee-b9c9-7b85db88f64e";
  }
}

function getAzureAlgoApiRequestScopes(): string {
  switch (getEnvironment()) {
    case "local":
      return "api://a144bf85-7251-4b46-9b1d-d3f46fba9a87/access_as_user";
    case "development":
      return "api://a144bf85-7251-4b46-9b1d-d3f46fba9a87/access_as_user";
    case "production":
      return "api://f60bf023-b90e-448f-9a62-2abf3642e399/access_as_user";
  }
}

/**
 * Resolved from exactly two places, in order:
 *
 *   1. The build-time override — `npm start -- local` — for local development.
 *   2. `window.__MFT_ENVIRONMENT__`, set by the `/config.js` that
 *      `public/index.html` loads ahead of the bundle. In a container that file
 *      is written at start-up from the `MFT_ENVIRONMENT` variable, so one image
 *      serves both dev and prod.
 *
 * There is deliberately no fallback. This used to guess from
 * `window.location.origin` and default to "development", which meant a new
 * origin — like this deployment — silently traded against the wrong lazerhub.
 * Refusing to load is the safer failure.
 */
export function getEnvironment(): Environment {
  const override = overrideEnvironment();
  if (override !== null) {
    return override;
  }

  const runtime = window.__MFT_ENVIRONMENT__;
  if (!isEnvironment(runtime)) {
    throw new Error(
      `Runtime environment is not configured: window.__MFT_ENVIRONMENT__ was ${JSON.stringify(
        runtime
      )}, expected one of ${ENVIRONMENTS.join(", ")}. It is set by /config.js, ` +
        `which the container generates on start-up from MFT_ENVIRONMENT.`
    );
  }

  return runtime;
}

export function isProduction() {
  return getEnvironment() === "production";
}

/**
 * Snapshot of the environment-dependent values, resolved at import time. Must
 * stay at the bottom of the file: these calls reach `isEnvironment`, a `const`,
 * so evaluating them any earlier hits its temporal dead zone.
 */
const MFTEnvironmentVariables = {
  azureAlgoApiRequestScopes: getAzureAlgoApiRequestScopes(),
  azureJepxDataProviderScopes: getAzureJepxDataProviderScopes(),
  azureClientId: getAzureClientId(),
  isProduction: isProduction(),
};

export { MFTEnvironmentVariables };
